import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { IApplicationState } from 'Store/types'
import { useTranslation } from 'react-i18next'
import {
  getUploadLink,
  uploadVehicleVisitingPointFiles,
  uploadVehicleVisitingPointFilesPodSpace,
} from 'Application/Visits/Actions/action'
import {
  setSkipVideo,
  setVehicleVisitingPointFileContent,
} from 'Application/Visits/Actions/reducer'
import Icon from 'Components/Icon'
import PodSpaceImage from 'Components/Image/PodSpaceImage'
import { useSettings } from 'Contexts/SettingsContext'
import Const from 'Utils/Consts'
import { useNetStatus } from 'Contexts/NetStatusContext'
import { VehicleVerification } from 'assets/vehicle_verification/vehicle_verification_helper/VehicleVerification'

const ImagesCheck = ({
  handleNext,
  handleBack,
}: {
  handleNext: () => void
  handleBack: () => void
}) => {
  const [t] = useTranslation()
  const { settings } = useSettings()
  const { netStatus } = useNetStatus()
  const dispatch = useDispatch()

  const videoFormat = settings?.REACT_APP_VIDEO_FORMAT || Const.env.REACT_APP_VIDEO_FORMAT
  const checkAi = settings?.REACT_APP_CHECK_AI ?? Const.env.REACT_APP_CHECK_AI
  const canSkipVideo = settings?.REACT_APP_CAN_SKIP_VIDEO ?? Const.env.REACT_APP_CAN_SKIP_VIDEO
  const uploadLinkSize =
    (settings?.REACT_APP_UPLOAD_LINK_SIZE ?? Const.env.REACT_APP_UPLOAD_LINK_SIZE) || 10200

  const {
    data,
    loading,
    recordedChunks,
    videoLoading,
    videoStatus,
    videoUploadProgress,
    skipVideo,
  } = useSelector((store: IApplicationState) => store.visits.newVisit.vehicleVisitingPoints)

  const { uploadLink, storeFileType } = useSelector(
    (store: IApplicationState) => store.visits.newVisit,
  )
  const { link } = uploadLink

  const videoPlayerRef = useRef<HTMLVideoElement>(null)
  const [vehicleVerification, setVehicleVerification] = useState<any>(null)
  const processedOnce = useRef(false)

  useEffect(() => {
    ;(async () => {
      try {
        const instance = await new VehicleVerification()
        setVehicleVerification(instance)
        console.log('✅ VehicleVerification initialized')
      } catch (err) {
        console.error('❌ Error initializing VehicleVerification:', err)
      }
    })()
  }, [])

  useEffect(() => {
    if (checkAi) {
      const processAllImages = async () => {
        if (processedOnce.current) return
        if (!vehicleVerification || !data?.length) return

        processedOnce.current = true
        console.log('🖼 شروع پردازش تصاویر...')

        for (let i = 0; i < data.length; i++) {
          const item = data[i]
          if (!item?.image) continue

          await new Promise<void>(resolve => {
            const img = new Image()
            img.src = item.image
            img.onload = async () => {
              const canvas = document.createElement('canvas')
              const ctx = canvas.getContext('2d')
              canvas.width = img.naturalWidth
              canvas.height = img.naturalHeight
              ctx?.drawImage(img, 0, 0)

              try {
                const result = await vehicleVerification.processResult(canvas, 0.0, null)
                console.log(`✅ عکس ${i + 1}:`, result)
              } catch (error) {
                console.error(`❌ خطا در عکس ${i + 1}:`, error)
              }
              resolve()
            }
            img.onerror = () => {
              console.error(`🚫 خطا در لود عکس ${i + 1}`)
              resolve()
            }
          })
        }

        console.log('🎯 تمام عکس‌ها پردازش شدند.')
      }

      processAllImages()
    }
  }, [vehicleVerification, data])

  useEffect(() => {
    if (recordedChunks?.length) {
      const blob = new Blob(recordedChunks, { type: videoFormat })
      const url = URL.createObjectURL(blob)
      if (videoPlayerRef.current) videoPlayerRef.current.src = url
    }
  }, [recordedChunks])

  const onNextHandleClick = () => {
    if (netStatus !== 'MANUAL_OFF') {
      if (storeFileType === 'POD_SPACE') {
        if (link) {
          dispatch(
            uploadVehicleVisitingPointFilesPodSpace({
              handleNext,
              videoFormat,
              uploadLink: link,
            }),
          )
        } else {
          dispatch(
            getUploadLink({
              data: { sizeInKB: uploadLinkSize, visitId: 0, type: 2 },
              handleNext,
              videoFormat,
            }),
          )
        }
      } else {
        dispatch(uploadVehicleVisitingPointFiles({ handleNext, videoFormat }))
      }
    } else handleNext()
  }

  return (
    <>
      <div className="flex flex-col grow overflow-auto">
        <div className="px-6 pb-6">
          <div className="grid grid-cols-2 gap-4 mb-5">
            {data.map((x, i) => (
              <div
                key={i}
                style={{ position: 'relative' }}
                className={loading && Number(x?.status) !== 1 ? 'loading' : ''}
              >
                <PodSpaceImage
                  isPodSpace={storeFileType === 'POD_SPACE'}
                  className="flex items-end w-40 h-36 rounded-lg border border-gray-600 bg-cover bg-no-repeat bg-center overflow-hidden"
                  hashFile={x?.hashFile}
                  fileId={x?.fileId}
                  fileContent={x?.image}
                  onLoad={(image: string) =>
                    dispatch(setVehicleVisitingPointFileContent({ id: x?.id, image }))
                  }
                >
                  <div className="bg-white p-2 text-center text-gray-900 text-xs w-full">
                    {x.name}
                  </div>
                </PodSpaceImage>
                {Number(x?.status) === 1 && (
                  <span
                    style={{
                      position: 'absolute',
                      top: 5,
                      right: 5,
                      fontSize: 10,
                    }}
                  >
                    {t('UPLOADED')}
                  </span>
                )}
                {loading &&
                  Number(x?.status) !== 1 &&
                  Number(x?.uploadProgress) > 0 &&
                  Number(x?.uploadProgress) < 100 && (
                    <span
                      style={{
                        position: 'absolute',
                        top: 5,
                        right: 5,
                        fontSize: 10,
                      }}
                    >
                      %{Math.round(Number(x?.uploadProgress))}
                    </span>
                  )}
              </div>
            ))}
          </div>

          {!skipVideo && (
            <div className={videoLoading && videoStatus !== 1 ? 'loading' : ''}>
              <video
                ref={videoPlayerRef}
                style={{ maxWidth: '100%', height: '250px' }}
                controls
                autoPlay={false}
              />
            </div>
          )}

          {Number(videoStatus) === 1 && (
            <span style={{ width: 100, fontSize: 10 }}>{t('UPLOADED')}</span>
          )}
          {videoLoading &&
            videoStatus !== 1 &&
            videoUploadProgress > 0 &&
            videoUploadProgress < 100 && (
              <span style={{ fontSize: 10 }}>{`${Math.round(videoUploadProgress)}%`}</span>
            )}
        </div>
      </div>

      <div className="cta">
        {canSkipVideo && !skipVideo && !data.some(x => x.status != 1) && videoStatus != 1 && (
          <button
            className="btn btn-info btn-lg flex gap-2 items-center justify-center w-full"
            onClick={() => dispatch(setSkipVideo(true))}
          >
            {t('SKIP_VIDEO')}
          </button>
        )}
        <div className="flex justify-between items-center gap-2">
          <button
            className="btn btn-warning btn-lg flex gap-2 items-center justify-center w-full"
            disabled={uploadLink.loading || loading || videoLoading}
            onClick={handleBack}
          >
            <Icon name="arrow-right" className="w-6 h-6" />
            {t('BACK')}
          </button>
          <button
            className="btn btn-primary btn-lg flex gap-2 items-center justify-center w-full"
            disabled={uploadLink.loading || loading || videoLoading}
            onClick={onNextHandleClick}
          >
            {t('NEXT')}
            <Icon name="arrow-left" className="w-6 h-6" />
          </button>
        </div>
      </div>
    </>
  )
}

export default ImagesCheck
